import { useEffect } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetReportTemplatesQuery } from 'apis/report-templates.api';
import { REPORT_TEMPLATES_TABLE_COLUMNS, TEMPLATES_DEFAULT_FILTERS_VALUES } from 'constants/reports-templates';
import { ROUTES } from 'constants/routes';
import { useFilters } from 'hooks/useFilters';
import { useOpen } from 'hooks/useOpen';
import { usePagination } from 'hooks/usePagination';
import { useTableSorting } from 'hooks/useTableSorting';
import { RolesFilterFormValues } from 'interfaces/roles.interface';
import { BaseFilterValues, CommonError } from 'interfaces/shared.interface';
import ReportTemplatesRow from 'page-components/report-templates/ReportTemplatesRow';
import { Button, ErrorModal, FilterForm, LoadingOverlay, Pagination, Table } from 'shared-components';

const ReportTemplates = () => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const [isErrorModalOpen, handleOpenErrorModal, handleCloseErrorModal] = useOpen();

  const { filterParams, applyFilters, isSameFilters } = useFilters();
  const { paginationParams, handleSetMaxResultCount, maxResultCount, handleSetCurrentPage, currentPage } =
    usePagination();
  const { handleSetTabName, sortingParams, sortingColumnId, sortingType } = useTableSorting();

  const {
    data,
    isLoading,
    isFetching,
    refetch,
    error: errorReportTemplatesData,
  } = useGetReportTemplatesQuery(`${filterParams}${sortingParams}${paginationParams}`);

  const { result } = data || {};
  const { error: errorData } = (errorReportTemplatesData as CommonError)?.data || {};

  const reportTemplatesFiltersMethods = useForm<RolesFilterFormValues>({
    defaultValues: TEMPLATES_DEFAULT_FILTERS_VALUES,
  });

  const handleClearFields = () => {
    reportTemplatesFiltersMethods.reset(TEMPLATES_DEFAULT_FILTERS_VALUES);
    onSubmitFilters(TEMPLATES_DEFAULT_FILTERS_VALUES);
  };

  const onSubmitFilters = async (data: BaseFilterValues) => {
    const { filter } = data;

    const newFiltersToApply = {
      NameQuery: filter,
    };

    if (isSameFilters(newFiltersToApply)) {
      refetch();
    } else {
      applyFilters(newFiltersToApply);
    }
  };

  const handleOpenCreateTemplatePage = () => {
    navigation(ROUTES.reportTemplatesCreate);
  };

  useEffect(() => {
    if (errorReportTemplatesData) {
      handleOpenErrorModal();
    }
  }, [handleOpenErrorModal, errorReportTemplatesData]);

  return (
    <div data-cy="report-templates-page" className="relative">
      <LoadingOverlay isLoading={isFetching && !!data} />

      <div>
        <FormProvider {...reportTemplatesFiltersMethods}>
          <form onSubmit={reportTemplatesFiltersMethods.handleSubmit(onSubmitFilters)}>
            <FilterForm
              dataCy="report-templates-page-search"
              placeHolder="SearchByName"
              handleClearFields={handleClearFields}
              withFilter={false}
              rightTopActionButton={
                <Button
                  onClick={handleOpenCreateTemplatePage}
                  data-cy="create-template-modal-button"
                  className="h-10 ml-3.5"
                  type="button"
                >
                  {t('Create')}
                </Button>
              }
            />
          </form>
        </FormProvider>
      </div>

      <Table
        dataCy="report-templates-table"
        columns={REPORT_TEMPLATES_TABLE_COLUMNS}
        handleSetTabName={handleSetTabName}
        sortingColumnId={sortingColumnId}
        sortingType={sortingType}
        isLoading={isLoading}
      >
        {result?.items.map((item) => (
          <ReportTemplatesRow key={item.id} reportTemplatesData={item} refetchTemplates={refetch} />
        ))}
      </Table>

      <Pagination
        totalCount={result?.totalCount}
        handleSetMaxResultCount={handleSetMaxResultCount}
        maxResultCount={maxResultCount}
        handleSetCurrentPage={handleSetCurrentPage}
        currentPage={currentPage}
      />

      <ErrorModal
        errorMessage={errorData?.message}
        description={errorData?.details}
        isOpen={isErrorModalOpen}
        handleClose={handleCloseErrorModal}
      />
    </div>
  );
};

export default ReportTemplates;
